import { rhytCardTypes } from "../../../../../common/parameters";
import { IBar, IScore } from "../../../../../common/types";
import parseBeams, { IGroup } from "../../../Score/score-display/Bar/beaming";
import { getLastSignature } from "../../../Score/score-display/utils";
import { $paddingLeft, $paddingRight } from "./Box/styles";

export const getRawRhyt = (rhyt: string[][]) =>
    rhyt.map(box => {
        let rawBox: number[] = [];
        box.forEach(note => {
            let value = rhytCardTypes[note].time;
            if (typeof value === "object") {
                value.forEach(v => rawBox.push(v));
            } else {
                rawBox.push(value);
            }
        });
        return rawBox;
    });

const totalLoop = 50;
export const randomizeOrder = ({ boxes }: { boxes: string[][] }, l: number = 0) => {
    let total = boxes.length;
    let order: number[] = [];
    let n;
    let i;

    if (total === 2) {
        return [1, 0];
    } else if (total === 1) {
        return [0];
    }
    for (let a = 0; a < total; a++) {
        while (order.length === a) {
            n = Math.floor(Math.random() * total);
            i = -1;
            for (let b = 0; b < order.length; b++) {
                if (order[b] === n) {
                    i = b;
                    break;
                }
            }

            if (i === -1) {
                order.push(n);
            }
        }
    }

    let totalSame = 0;
    for (let a = 0; a < total; a++) {
        if (order[a] === a) {
            totalSame++;
        }
    }
    if (l > totalLoop) {
        return order;
    }
    l++;
    if (totalSame >= total - 1 && boxes.length > 3) {
        order = randomizeOrder({ boxes }, l);
    }

    // by rhythm
    totalSame = 0;
    for (let a = 0; a < total; a++) {
        if (boxes[order[a]].join("-") === boxes[a].join("-")) {
            totalSame++;
        }
    }
    if (totalSame === total) {
        order = randomizeOrder({ boxes }, l);
    }

    return order;
};

const defaultThreshold = 50;

export const isNearAnyDrop = (drops: { x: number, y: number }[], left: number, top: number, threshold = defaultThreshold) => {
    let target;
    drops.forEach((drop, index) => {
        if (Math.abs(drop.y - top) < threshold && Math.abs(drop.x - left) < threshold) {
            target = index;
        }
    });
    return typeof target === "number" ? target : null;
};

export const getWidth = (time: number | number[]) => {
    if (typeof time === "number") return Math.abs(time);
    return time.reduce((a, b) => a + b);
};

export const getBoxWidth = (box: string[]) => {
    let width = 0;
    box.forEach((rhyt) => {
        width += (rhytCardTypes[rhyt].width || getWidth(rhytCardTypes[rhyt].time)) * 65;
    });
    width +=$paddingLeft + $paddingRight;
    return width;
};

export const getBoxWidths = (boxes: string[][]) => {
    const widths: { time: number, width: number }[] = [];
    const similarWidths: { time: number, widths: Set<number> }[] = [];
    boxes.forEach(box => {
        const time = box.reduce((total, rhythm) => total + getWidth(rhytCardTypes[rhythm].time), 0);
        const width = getBoxWidth(box);
        widths.push({
            time,
            width,
        });
        const similarWidth = similarWidths.find(sW => sW.time === time);
        if (similarWidth) {
            similarWidth.widths.add(width);
        } else {
            similarWidths.push({
                time,
                widths: new Set<number>().add(width),
            });
        }
    });
    widths.forEach(width => {
        width.width = Math.max.apply(null, [...similarWidths.find(sW => sW.time === width.time)!.widths]);
    });
    return widths.map(width => width.width);
};

export const parseScoreToBoxes = (score: IScore) => {
    const bars: string[][] = [];
    const breaks: number[] = [];
    let isValidRhythms = true;
    score.bars.forEach((bar, barIndex) => {
        if (!bar || !bar.notes || !bar.notes.length) {
            return;
        };
        const { notes } = bar;
        const signature = getLastSignature(score.signatures, barIndex, score.bars as IBar[], score);
        const groups = parseBeams({ notes, signature, pitchPositions: [], onlyGroup: true }) as IGroup[][];
        const parsedBar: string[] = [];
        groups.forEach(group => {
            const cardValues = group.map(note => {
                const length = 4 / note.note.l * (note.note.d ? 1.5 : 1) * (note.note.r ? -1 : 1) * (note.note.t ? 0.66 : 1) + (note.note.ts ? 0.01 : 0);
                return length;
            });
            if (cardValues.length === 0) return;
            const rhytCardType = Object.keys(rhytCardTypes).find(rhytType => {
                const type = rhytCardTypes[rhytType];
                if (cardValues.length === 1) {
                    if (typeof type.time === "number" && type.time === cardValues[0]) {
                        return true;
                    }
                } else if (Array.isArray(type.time) && type.time.length === cardValues.length) {
                    if (cardValues.join(",") === type.time.join(",")) {
                        return true;
                    }
                }
                return false;
            });
            if (rhytCardType) {
                parsedBar.push(rhytCardType);
            } else {
                isValidRhythms = false;
            }
        });
        bars.push(parsedBar);
        if ((score.barlines || []).find(bl => bl.bar === barIndex)?.break) {
            breaks.push(bars.length - 1);
        }
    });
    return {
        bars,
        breaks,
        isValidRhythms,
    }
}